import {
  DEFAULT_STC_CONFIG,
  loadConventionKey,
  loadStcConfig,
  saveConventionKey,
  saveStcConfig,
  type StcConfig,
} from '@application/rh/StcConfig';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';

export interface AncienneteConfig {
  /** Progression annuelle (0,01 = +1 % par an) ; sans `paliers` : taux appliqué par année d'ancienneté. */
  taux_par_an: number;
  debut_annee: number;
  plafond: number;
  /**
   * Barème par paliers (années d'ancienneté révolues → taux de la prime). Au-delà du dernier
   * palier, la prime progresse de `taux_par_an` par année. Absent = formule `taux_par_an × années`.
   */
  paliers?: readonly { annees: number; taux: number }[];
}

export interface ConventionStcOverride {
  convention?: string;
  conges?: Partial<StcConfig['conges']>;
  treizieme?: Partial<StcConfig['treizieme']>;
  precarite_cdd?: Partial<StcConfig['precarite_cdd']>;
  preavis?: Partial<StcConfig['preavis']>;
  licenciement?: Partial<StcConfig['licenciement']>;
  base?: Partial<StcConfig['base']>;
}

export interface ConventionCollective {
  key: string;
  label: string;
  secteur: string;
  annee: number;
  anciennete: AncienneteConfig | null;
  stc: ConventionStcOverride;
  notes: string;
  /** Références documentaires Gabon (pas de CCN étrangères). */
  sources: readonly string[];
}

export interface ConventionResume {
  prime_anciennete: string;
  plafond_anciennete: string;
  conges: string;
  indemnite_licenciement: string;
  treizieme: string;
}

/**
 * Barème conventionnel courant (tronc commun A.30 et 10 conventions sectorielles) : 2 % après 2 ans
 * d'ancienneté, puis +1 % par an (n % à n ans). Plafond 30 % conservé par prudence (seule la
 * convention des Banques l'écrit explicitement) — à valider.
 */
const ANC2: AncienneteConfig = {
  taux_par_an: 0.01,
  debut_annee: 2,
  plafond: 0.3,
  paliers: [{ annees: 2, taux: 0.02 }],
};

/** Pétrole et Banques : 5 % après 2 ans, puis +1 % par an (Banques : 30 % maximum à 27 ans). */
const ANC_PETROLE: AncienneteConfig = {
  taux_par_an: 0.01,
  debut_annee: 2,
  plafond: 0.3,
  paliers: [{ annees: 2, taux: 0.05 }],
};

/** Assurances (A.30) : 2 % après 2 ans, 5 % après 3 ans, puis +1 % par an. */
const ANC_ASSURANCES: AncienneteConfig = {
  taux_par_an: 0.01,
  debut_annee: 2,
  plafond: 0.3,
  paliers: [
    { annees: 2, taux: 0.02 },
    { annees: 3, taux: 0.05 },
  ],
};

/**
 * Mines (A.30) : 3 % après 2 ans, puis barème par année d'ancienneté jusqu'à 25 % à 20 ans, puis
 * +1 % par an « dès 23 ans » (lecture : 25 % de 20 à 22 ans, 26 % à 23 ans, 27 % à 24 ans…).
 */
const ANC_MINES: AncienneteConfig = {
  taux_par_an: 0.01,
  debut_annee: 2,
  plafond: 0.3,
  paliers: [
    { annees: 2, taux: 0.03 },
    { annees: 3, taux: 0.04 },
    { annees: 4, taux: 0.05 },
    { annees: 5, taux: 0.07 },
    { annees: 6, taux: 0.08 },
    { annees: 7, taux: 0.09 },
    { annees: 8, taux: 0.1 },
    { annees: 9, taux: 0.11 },
    { annees: 10, taux: 0.12 },
    { annees: 11, taux: 0.13 },
    { annees: 12, taux: 0.14 },
    { annees: 13, taux: 0.15 },
    { annees: 14, taux: 0.17 },
    { annees: 15, taux: 0.18 },
    { annees: 16, taux: 0.19 },
    { annees: 17, taux: 0.2 },
    { annees: 18, taux: 0.21 },
    { annees: 19, taux: 0.22 },
    { annees: 20, taux: 0.25 },
    { annees: 23, taux: 0.26 },
  ],
};

// Les barèmes conventionnels ne peuvent pas être inférieurs au minimum légal (art. 87 et 90 du Code
// du travail : 20 % par année, sans ancienneté minimale, fractions d'année comprises).
const LIC_FORESTIERES: StcConfig['licenciement'] = {
  anciennete_min_annees: 0,
  annees_entieres: false,
  tranches: [
    { jusqua: 5, taux: 0.2 },
    { jusqua: 10, taux: 0.22 },
    { jusqua: 15, taux: 0.25 },
    { jusqua: 20, taux: 0.28 },
    { jusqua: 999, taux: 0.3 },
  ],
};

const LIC_20: StcConfig['licenciement'] = {
  anciennete_min_annees: 0,
  annees_entieres: false,
  tranches: [
    { jusqua: 5, taux: 0.2 },
    { jusqua: 10, taux: 0.25 },
    { jusqua: 999, taux: 0.3 },
  ],
};

const SRC_CODE = 'Code du travail gabonais — Loi n° 3/94 du 21 novembre 1994 (modifiée)';
const SRC_FAOLEX_BOIS = 'FAOLEX gab207593 — Convention collective industries du bois (Gabon)';
const SRC_FAOLEX_FORET = 'FAOLEX gab208129 — Convention collective exploitations forestières (Gabon)';

export const CONVENTIONS: readonly ConventionCollective[] = [
  {
    key: 'general',
    label: 'Régime général — Code du travail (Loi 3/94)',
    secteur: 'Défaut',
    annee: 1994,
    anciennete: null,
    stc: {},
    notes:
      'Régime par défaut : dispositions du Code du travail gabonais. Aucune majoration sectorielle.',
    sources: [SRC_CODE],
  },
  {
    key: 'commerce',
    label: 'Commerce (1983)',
    secteur: 'Commerce',
    annee: 1983,
    anciennete: ANC2,
    stc: {
      convention: 'Commerce (1983)',
      treizieme: { actif: false, prorata: false },
      licenciement: LIC_20,
    },
    notes:
      "Prime d'ancienneté : 2 % après 2 ans puis +1 %/an. Congés exceptionnels jusqu'à 10 j/an non déductibles. Gratification de fin d'année DISCRÉTIONNAIRE (Art. A.49.5) : pas de 13ᵉ mois automatique.",
    sources: [SRC_CODE, 'Convention collective Commerce Gabon (1983)'],
  },
  {
    key: 'minieres',
    label: 'Entreprises minières et assimilées (1984)',
    secteur: 'Mines',
    annee: 1984,
    anciennete: ANC_MINES,
    stc: {
      convention: 'Mines (1984)',
      conges: { jours_par_an: 30 },
      treizieme: { actif: true, prorata: true },
      licenciement: LIC_20,
    },
    notes:
      "Congés : 2,5 jours ouvrables par mois (30 j/an). 13ᵉ mois (Art. A.55). Prime d'ancienneté : barème de la convention (3 % à 2 ans, 25 % à 20 ans, A.30).",
    sources: [SRC_CODE, 'Convention collective entreprises minières et assimilées (1984)'],
  },
  {
    key: 'banques',
    label: 'Banques et Établissements Financiers (1984)',
    secteur: 'Banque/Finance',
    annee: 1984,
    anciennete: ANC_PETROLE,
    stc: {
      convention: 'Banques (1984)',
      conges: { jours_par_an: 26 },
      treizieme: { actif: true, prorata: true },
      licenciement: LIC_20,
    },
    notes:
      "Gratification de fin d'année = 1 mois de salaire (≥ 1 an, prorata sinon — Art. A.53). Congés : +2 j ouvrables après 5 ans d'ancienneté (à ajuster manuellement si besoin).",
    sources: [SRC_CODE, 'Convention collective Banques et Établissements Financiers (1984)'],
  },
  {
    key: 'assurances',
    label: 'Assurances (1983)',
    secteur: 'Assurances',
    annee: 1983,
    anciennete: ANC_ASSURANCES,
    stc: {
      convention: 'Assurances (1983)',
      treizieme: { actif: true, prorata: true },
      licenciement: LIC_20,
    },
    notes:
      "Prime d'ancienneté : 2 % après 2 ans, 5 % après 3 ans, puis +1 %/an (A.30). 13ᵉ mois (Art. A.50). Prime de panier selon usage.",
    sources: [SRC_CODE, 'Convention collective Assurances (1983)'],
  },
  {
    key: 'forestieres',
    label: 'Exploitations forestières (1985)',
    secteur: 'Forêt',
    annee: 1985,
    anciennete: ANC2,
    stc: {
      convention: 'Forestières (1985)',
      licenciement: LIC_FORESTIERES,
      treizieme: { actif: false, prorata: false },
    },
    notes:
      "Indemnité de services rendus / licenciement par tranches (20 % → 30 %). Gratification discrétionnaire (Art. A.51) : pas de 13ᵉ mois automatique. Prime d'ancienneté 2 % après 2 ans puis +1 %/an.",
    sources: [SRC_CODE, SRC_FAOLEX_FORET],
  },
  {
    key: 'bois',
    label: 'Industries du bois (1983)',
    secteur: 'Bois',
    annee: 1983,
    anciennete: ANC2,
    stc: {
      convention: 'Industries du bois (1983)',
      treizieme: { actif: false, prorata: false },
      licenciement: LIC_20,
    },
    notes:
      "Prime d'ancienneté 2 % après 2 ans puis +1 %/an. Gratification de fin d'année (Art. A.52) : 25 % fixe d'un maximum + part variable discrétionnaire, pas de 13ᵉ mois automatique.",
    sources: [SRC_CODE, SRC_FAOLEX_BOIS],
  },
  {
    key: 'portuaire',
    label: 'Secteurs portuaires, maritimes et fluviaux (1985)',
    secteur: 'Portuaire',
    annee: 1985,
    anciennete: ANC2,
    stc: {
      convention: 'Portuaire/maritime (1985)',
      treizieme: { actif: false, prorata: false },
      licenciement: LIC_20,
    },
    notes:
      "Prime d'ancienneté 2 % après 2 ans puis +1 %/an. Gratification de fin d'année discrétionnaire (Art. A.56) : pas de 13ᵉ mois automatique. Primes de risque/panier/transport exclues de l'allocation de congé.",
    sources: [SRC_CODE, 'Convention collective portuaire, maritime et fluviale (1985)'],
  },
  {
    key: 'petrole',
    label: 'Pétrole et gaz (1983)',
    secteur: 'Pétrole',
    annee: 1983,
    anciennete: ANC_PETROLE,
    stc: {
      convention: 'Pétrole et gaz (1983)',
      conges: { jours_par_an: 26 },
      licenciement: LIC_20,
      treizieme: { actif: false, prorata: false },
    },
    notes:
      "Groupement des professionnels du pétrole — prime d'ancienneté majorée (5 % après 2 ans puis +1 %/an). Prime de fin d'année bénévole (A.54) : pas de 13ᵉ mois automatique. Indemnité de licenciement par tranches.",
    sources: [SRC_CODE, 'Convention collective Groupement des professionnels du pétrole (Gabon, 1983)'],
  },
  {
    key: 'btp',
    label: 'Bâtiments et Travaux Publics — BTP (1983)',
    secteur: 'BTP',
    annee: 1983,
    anciennete: ANC2,
    stc: {
      convention: 'BTP (1983)',
      treizieme: { actif: false, prorata: false },
      licenciement: LIC_20,
    },
    notes:
      "Prime d'ancienneté 2 % après 2 ans puis +1 %/an. Gratification discrétionnaire (Art. A.49.4) : pas de 13ᵉ mois automatique. Primes de chantier/déplacement selon usage.",
    sources: [SRC_CODE, 'Convention collective BTP Gabon (1983)'],
  },
  {
    key: 'hotellerie',
    label: 'Hôtellerie - Restauration - Débit de boisson (1986)',
    secteur: 'Hôtellerie',
    annee: 1986,
    anciennete: ANC2,
    stc: {
      convention: 'Hôtellerie (1986)',
      treizieme: { actif: false, prorata: false },
      licenciement: LIC_20,
    },
    notes:
      "Prime d'ancienneté 2 % après 2 ans puis +1 %/an. Prime de fin d'année (Art. A.50) : 30 % fixe + part variable discrétionnaire, pas de 13ᵉ mois automatique. Avantages en nature (nourriture/logement) selon usage.",
    sources: [SRC_CODE, 'Convention collective Hôtellerie-Restauration (1986)'],
  },
  {
    key: 'energie',
    label: "Production/transport/distribution d'énergie électrique et d'eau (1994)",
    secteur: 'Énergie/Eau',
    annee: 1994,
    anciennete: ANC2,
    stc: {
      convention: 'Énergie & Eau (1994)',
      licenciement: LIC_20,
      treizieme: { actif: false, prorata: false },
    },
    notes:
      "Indemnité de licenciement ≈ 20 % de la moyenne mensuelle du salaire global (12 derniers mois) par année, puis tranches. Prime d'ancienneté 2 % après 2 ans puis +1 %/an. Aucun 13ᵉ mois ni gratification trouvé dans la convention.",
    sources: [SRC_CODE, 'Convention collective énergie électrique et eau (1994)'],
  },
  {
    key: 'industrielles',
    label: 'Entreprises industrielles du Gabon (1983)',
    secteur: 'Industrie',
    annee: 1983,
    anciennete: ANC2,
    stc: {
      convention: 'Industrielles (1983)',
      licenciement: LIC_20,
      treizieme: { actif: false, prorata: false },
    },
    notes:
      "Indemnité de licenciement par tranches dès 2 ans. Gratification de fin d'année discrétionnaire (Art. A.51) — 13ᵉ mois non automatique.",
    sources: [SRC_CODE, 'Convention collective entreprises industrielles (1983)'],
  },
  {
    key: 'aerien',
    label: 'Compagnies de transports aériens du Gabon',
    secteur: 'Aérien',
    annee: 0,
    anciennete: ANC2,
    stc: {
      convention: 'Transports aériens',
      licenciement: LIC_20,
      treizieme: { actif: true, prorata: true },
    },
    notes:
      "Paramètres alignés sur le régime sectoriel courant (ancienneté 2 % après 2 ans puis +1 %/an ; gratification de fin d'année ≈ salaire de base, conditionnelle aux résultats). Affiner via le STC si accord d'entreprise spécifique.",
    sources: [SRC_CODE, 'Accords / conventions transports aériens Gabon'],
  },
  {
    key: 'cnss',
    label: "CNSS — convention d'établissement (1986)",
    secteur: 'Para-public',
    annee: 1986,
    anciennete: ANC2,
    stc: {
      convention: 'CNSS (1986)',
      conges: { jours_par_an: 30 },
      treizieme: { actif: true, prorata: true },
      licenciement: LIC_20,
    },
    notes: "Convention d'établissement CNSS — congés 30 j/an, 13ᵉ mois (A.46), ancienneté 2 % après 2 ans puis +1 %/an (taux de la convention absents de la base : à vérifier).",
    sources: [SRC_CODE, "Convention d'établissement CNSS (1986)"],
  },
];

const ANCIENNETE_NS = 'rh.anciennete_config';

export function getConventionByKey(key: string): ConventionCollective {
  return CONVENTIONS.find((c) => c.key === key) ?? CONVENTIONS[0]!;
}

/** Résout une clé ou un ancien libellé libre vers une clé catalogue. */
export function resolveConventionKey(raw: string | undefined | null): string {
  if (!raw) return 'general';
  const byKey = CONVENTIONS.find((c) => c.key === raw);
  if (byKey) return byKey.key;
  const lower = raw.toLowerCase();
  const byLabel = CONVENTIONS.find(
    (c) =>
      c.label.toLowerCase() === lower ||
      c.stc.convention?.toLowerCase() === lower ||
      lower.includes(c.secteur.toLowerCase()),
  );
  if (byLabel) return byLabel.key;
  if (lower.includes('pétrole') || lower.includes('petrole') || lower.includes('gaz')) {
    return 'petrole';
  }
  if (lower.includes('mine')) return 'minieres';
  if (lower.includes('banque')) return 'banques';
  if (lower.includes('commerce')) return 'commerce';
  if (lower.includes('btp') || lower.includes('bâtiment') || lower.includes('batiment')) {
    return 'btp';
  }
  if (lower.includes('bois') || lower.includes('forêt') || lower.includes('foret')) {
    return lower.includes('bois') ? 'bois' : 'forestieres';
  }
  return 'general';
}

export function buildStcFromConvention(conv: ConventionCollective): StcConfig {
  const cfg: StcConfig = JSON.parse(JSON.stringify(DEFAULT_STC_CONFIG)) as StcConfig;
  cfg.convention = conv.label;
  const ov = conv.stc;
  if (ov.convention) cfg.convention = ov.convention;
  if (ov.base) cfg.base = { ...cfg.base, ...ov.base };
  if (ov.licenciement) {
    cfg.licenciement = {
      ...cfg.licenciement,
      ...ov.licenciement,
      tranches: ov.licenciement.tranches
        ? [...ov.licenciement.tranches]
        : cfg.licenciement.tranches,
    };
  }
  if (ov.preavis) cfg.preavis = { ...cfg.preavis, ...ov.preavis };
  if (ov.conges) cfg.conges = { ...cfg.conges, ...ov.conges };
  if (ov.precarite_cdd) cfg.precarite_cdd = { ...cfg.precarite_cdd, ...ov.precarite_cdd };
  if (ov.treizieme) cfg.treizieme = { ...cfg.treizieme, ...ov.treizieme };
  return cfg;
}

function pct(rate: number): string {
  return `${Math.round(rate * 1000) / 10} %`;
}

/** Résumé lisible pour l’UI Entreprise / Paramètres (toujours dérivé du catalogue). */
export function resumeConvention(key: string): ConventionResume {
  const conv = getConventionByKey(key);
  const stc = key === 'general' ? DEFAULT_STC_CONFIG : buildStcFromConvention(conv);
  const anc = conv.anciennete;

  const premier = anc?.paliers?.[0];
  const prime_anciennete = anc
    ? premier
      ? `${pct(premier.taux)} après ${premier.annees} ans, puis progression (+${pct(anc.taux_par_an)} par an)`
      : `${pct(anc.taux_par_an)} par année à partir de la ${anc.debut_annee}ᵉ année`
    : 'Selon Code du travail / usage (pas de barème sectoriel forcé)';

  const plafond_anciennete = anc ? `${pct(anc.plafond)} maximum` : '—';

  const conges = `${stc.conges.jours_par_an} jours ouvrables`;

  const t0 = stc.licenciement.tranches[0];
  const indemnite_licenciement = t0
    ? `${pct(t0.taux)} du salaire / année${
        stc.licenciement.anciennete_min_annees > 0
          ? ` (dès ${stc.licenciement.anciennete_min_annees} ans)`
          : ' (sans ancienneté minimale)'
      }`
    : 'Selon Code du travail';

  const treizieme = stc.treizieme.actif
    ? stc.treizieme.prorata
      ? 'Oui — 1 mois (prorata si < 1 an)'
      : 'Oui — 1 mois'
    : 'Selon usage / convention (non automatique)';

  return {
    prime_anciennete,
    plafond_anciennete,
    conges,
    indemnite_licenciement,
    treizieme,
  };
}

/** Anciens barèmes « 2 %/an » (doublés) enregistrés avant les réponses légales du 16/10/2026. */
function estBaremeHeritage(c: AncienneteConfig): boolean {
  return (
    !c.paliers &&
    c.debut_annee === 2 &&
    c.plafond === 0.3 &&
    (c.taux_par_an === 0.02 || c.taux_par_an === 0.05)
  );
}

export async function loadAncienneteConfig(tenantId: string): Promise<AncienneteConfig | null> {
  const stored = await SecureTenantStore.getJson<AncienneteConfig | null>(
    tenantId,
    ANCIENNETE_NS,
    null,
  );
  if (!stored || !estBaremeHeritage(stored)) return stored;
  // Migration : barème hérité du catalogue → barème conventionnel courant de la convention choisie.
  const key = await loadConventionKey(tenantId);
  return getConventionByKey(key).anciennete ?? stored;
}

export async function saveAncienneteConfig(
  tenantId: string,
  config: AncienneteConfig | null,
): Promise<void> {
  await SecureTenantStore.setJson(tenantId, ANCIENNETE_NS, config);
}

/** Applique une convention : surcharge ancienneté + paramètres STC ; reste éditable. */
export async function appliquerConvention(tenantId: string, key: string): Promise<StcConfig> {
  const conv = getConventionByKey(key);
  await saveConventionKey(tenantId, conv.key);
  await saveAncienneteConfig(tenantId, conv.anciennete);

  if (key === 'general') {
    return saveStcConfig(tenantId, DEFAULT_STC_CONFIG);
  }
  return saveStcConfig(tenantId, buildStcFromConvention(conv));
}

export async function getConventionActive(tenantId: string): Promise<string> {
  return SecureTenantStore.getJson<string>(tenantId, 'rh.convention_key', 'general');
}

export async function loadConventionContext(tenantId: string): Promise<{
  key: string;
  convention: ConventionCollective;
  stcConfig: StcConfig;
  ancienneteConfig: AncienneteConfig | null;
}> {
  const key = await getConventionActive(tenantId);
  const convention = getConventionByKey(key);
  const [stcConfig, ancienneteConfig] = await Promise.all([
    loadStcConfig(tenantId),
    loadAncienneteConfig(tenantId),
  ]);
  return { key, convention, stcConfig, ancienneteConfig };
}
